import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ScheduleStatus from './ScheduleStatus';
import SurfConditionsDashboard from './SurfConditionsDashboard';
import PushNotificationManager from './PushNotificationManager';
import NotificationSender from './NotificationSender';
import apiClient from '../utils/apiClient';
import { safeLocalStorage } from '../utils/safeFunctions';

const Dashboard = () => {
  const { user } = useAuth();
  const [forecast, setForecast] = useState(null);
  const [forecastLoading, setForecastLoading] = useState(true);
  const [stormClosure, setStormClosure] = useState(null);
  const [stormClosureLoading, setStormClosureLoading] = useState(false);
  const [runSheetLoading, setRunSheetLoading] = useState(false);
  const [thunderstormAlerts, setThunderstormAlerts] = useState(null);
  const [nwsAlerts, setNwsAlerts] = useState(null);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [runSheetDate, setRunSheetDate] = useState(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [userPermissions, setUserPermissions] = useState([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  useEffect(() => {
    const fetchUserPermissions = async () => {
      try {
        console.log('🔍 Dashboard: Fetching user permissions...');
        const response = await apiClient.get('/api/user/permissions');
        console.log('🔍 Dashboard: Received permissions:', response.data);
        setUserPermissions(response.data);
        setPermissionsLoaded(true);
      } catch (error) {
        console.error('Failed to fetch user permissions:', error);
        setPermissionsLoaded(true);
      }
    };

    if (user) {
      fetchUserPermissions();
    }
  }, [user]);

  const canAccessUserManagement = () => {
    if (!permissionsLoaded) {
      // Conservative: hide during loading to prevent showing unauthorized content
      return false;
    }
    return userPermissions.includes('view_users') || userPermissions.includes('manage_beaches') || userPermissions.includes('manage_vehicles');
  };

  const canManageStormClosure = () => {
    if (!permissionsLoaded) {
      // Conservative: hide during loading to prevent unauthorized access
      return false;
    }
    return userPermissions.includes('manage_storm_closure');
  };

  const canGenerateRunSheet = () => {
    if (!permissionsLoaded) {
      // Conservative: hide during loading to prevent unauthorized access
      return false;
    }
    return userPermissions.includes('generate_reports');
  };

  // Update assigned beach flag quick action
  const [updatingFlag, setUpdatingFlag] = useState(false);
  const updateMyBeachFlag = async (flag) => {
    setUpdatingFlag(true);
    try {
      const token = localStorage.getItem('token');
      await apiClient.put('/api/my/beach-flag', { flag_status: flag }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      alert(`Beach flag set to ${flag}`);
    } catch (e) {
      console.error('Failed to update beach flag', e);
      alert(e.response?.data?.error || 'Failed to update beach flag');
    } finally {
      setUpdatingFlag(false);
    }
  };

  // Admin/LT: set all beach flags
  const canSetAllFlags = () => {
    if (!permissionsLoaded) return false;
    return userPermissions.includes('set_all_beach_flags');
  };
  const [settingAll, setSettingAll] = useState(false);
  const setAllFlags = async (flag) => {
    setSettingAll(true);
    try {
      const token = localStorage.getItem('token');
      await apiClient.put('/api/admin/beach-flags', { flag_status: flag }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      alert(`All beaches set to ${flag}`);
    } catch (e) {
      console.error('Failed to set all beach flags', e);
      alert(e.response?.data?.error || 'Failed to set all beach flags');
    } finally {
      setSettingAll(false);
    }
  };

  const formatBoolean = (value) => value ? 'Yes' : 'No';

  // Fetch weather forecast data (1-hour cache)
  const fetchForecastData = async () => {
    try {
      console.log('🌤️ Fetching forecast for dashboard');
      
    const response = await apiClient.get('/api/public/weather/forecast', {
      timeout: 15000,
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      },
      params: { _: Date.now() }
    });
      
      console.log('Dashboard forecast data received:', response.data);
      setForecast(response.data);
    } catch (error) {
      console.warn('Failed to fetch forecast data for dashboard:', error.message);
      
      // Set default forecast data if API fails
      setForecast({
        hourly: Array.from({ length: 7 }, (_, i) => ({
          time: new Date(Date.now() + i * 60 * 60 * 1000).toISOString(),
          values: {
            temperature: 22 + Math.random() * 6,
            humidity: 60 + Math.random() * 20,
            windSpeed: 2 + Math.random() * 4,
            uvIndex: Math.max(0, 6 - Math.abs(i - 6)),
            weatherCode: 1101,
            precipitationProbability: Math.random() * 30
          }
        })),
        error: true
      });
    } finally {
      setForecastLoading(false);
    }
  };

  // Fetch storm closure status
  const fetchStormClosureStatus = async () => {
    try {
      const response = await apiClient.get('/api/public/storm-closure');
      setStormClosure(response.data);
      console.log('Storm closure status:', response.data);
    } catch (error) {
      console.warn('Failed to fetch storm closure status:', error.message);
      setStormClosure({ isActive: false, closure: null });
    }
  };

  // Fetch thunderstorm alerts
  const fetchThunderstormAlerts = async () => {
    try {
      console.log('⚡ Fetching thunderstorm alerts for dashboard');
      const response = await apiClient.get('/api/public/thunderstorm-alerts', {
        timeout: 10000,
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        params: { _: Date.now() }
      });
      setThunderstormAlerts(response.data);
      console.log('Thunderstorm alerts:', response.data);
    } catch (error) {
      console.warn('Failed to fetch thunderstorm alerts:', error.message);
      setThunderstormAlerts({ 
        hasAlert: false, 
        alertText: 'Error fetching thunderstorm alerts',
        error: true 
      });
    }
  };

  // Fetch NWS alerts
  const fetchNwsAlerts = async () => {
    try {
      console.log('🚨 Fetching NWS alerts for dashboard');
      const response = await apiClient.get('/api/public/nws-alerts', {
        timeout: 10000,
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        params: { _: Date.now() }
      });
      setNwsAlerts(response.data);
      console.log('NWS alerts:', response.data);
    } catch (error) {
      console.warn('Failed to fetch NWS alerts:', error.message);
      setNwsAlerts({ 
        hasAlerts: false, 
        alertsText: 'Error fetching NWS alerts',
        count: 0,
        error: true 
      });
    }
  };

  // Fetch all weather alerts
  const fetchWeatherAlerts = async () => {
    setAlertsLoading(true);
    try {
      await Promise.all([
        fetchThunderstormAlerts(),
        fetchNwsAlerts()
      ]);
    } catch (error) {
      console.error('Failed to fetch weather alerts:', error);
    } finally {
      setAlertsLoading(false);
    }
  };

  // Close beaches due to storm
  const closeBeaches = async () => {
    const reason = prompt('Enter reason for beach closure (e.g., "Severe thunderstorm with dangerous lightning"):');
    if (!reason || reason.trim() === '') {
      return;
    }

    setStormClosureLoading(true);
          try {
        const response = await apiClient.post('/api/storm-closure/close', 
          { reason: reason.trim() }
        );
      
      alert('Beaches have been closed due to storm conditions');
      await fetchStormClosureStatus(); // Refresh status
    } catch (error) {
      console.error('Failed to close beaches:', error);
      alert(error.response?.data?.error || 'Failed to close beaches');
    } finally {
      setStormClosureLoading(false);
    }
  };

  // Reopen beaches after storm
  const reopenBeaches = async () => {
    const confirmed = window.confirm('Are you sure you want to reopen the beaches? Make sure storm conditions have cleared.');
    if (!confirmed) return;

    setStormClosureLoading(true);
          try {
        const response = await apiClient.post('/api/storm-closure/reopen');
      
      alert('Beaches have been reopened');
      await fetchStormClosureStatus(); // Refresh status
    } catch (error) {
      console.error('Failed to reopen beaches:', error);
      alert(error.response?.data?.error || 'Failed to reopen beaches');
    } finally {
      setStormClosureLoading(false);
    }
  };

  // Generate Run Sheet PDF for selected date
  const generateRunSheet = async () => {
    setRunSheetLoading(true);
          try {
        console.log('🚀 Starting Run Sheet generation for:', runSheetDate);
        
        const response = await apiClient.post('/api/generate-run-sheet', 
          { date: runSheetDate },
          { 
            responseType: 'blob',
            timeout: 30000
          }
        );
      
      console.log('📄 Received response:', {
        size: response.data.size,
        type: response.data.type,
        status: response.status,
        headers: response.headers
      });
      
      // Validate blob
      if (!response.data || response.data.size === 0) {
        throw new Error('Received empty PDF data');
      }
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      console.log('📁 Created blob:', { size: blob.size, type: blob.type });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `RunSheet_${runSheetDate}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ Run Sheet download initiated successfully');
      
    } catch (error) {
      console.error('❌ Failed to generate run sheet:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      alert(error.response?.data?.error || error.message || 'Failed to generate run sheet');
    } finally {
      setRunSheetLoading(false);
    }
  };

  useEffect(() => {
    fetchForecastData();
    fetchStormClosureStatus();
    fetchWeatherAlerts();
    
    // Refresh alerts every 5 minutes
    const alertsInterval = setInterval(() => {
      fetchWeatherAlerts();
    }, 5 * 60 * 1000);

    return () => {
      clearInterval(alertsInterval);
    };
  }, []);

  // Get weather icon based on weather code
  const getWeatherIcon = (code) => {
    const icons = {
      1000: '☀️', 1100: '🌤️', 1101: '⛅', 1102: '🌥️', 1001: '☁️',
      2000: '🌫️', 2100: '🌫️', 4000: '🌦️', 4001: '🌧️', 4200: '🌦️',
      4201: '⛈️', 5000: '❄️', 5001: '🌨️', 5100: '🌨️', 5101: '❄️',
      6000: '🌨️', 6001: '🌨️', 6200: '🌨️', 6201: '🌨️', 7000: '🌨️',
      7101: '🌨️', 7102: '🌨️', 8000: '⛈️'
    };
    return icons[code] || '🌤️';
  };

  // Convert Celsius to Fahrenheit
  const celsiusToFahrenheit = (celsius) => {
    return Math.round(celsius * 9/5 + 32);
  };

  // Convert m/s to mph
  const msToMph = (ms) => {
    return Math.round(ms * 2.237);
  };

  // Format time for display
  const formatTime = (timeString) => {
    return new Date(timeString).toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      hour12: true 
    });
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-subtitle">
          Welcome back, {user?.first_name && user?.last_name 
            ? `${user.first_name} ${user.last_name}` 
            : user?.username || 'User'}
        </p>
      </div>

      <ScheduleStatus />

      {/* Weather Alerts */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">🚨 Weather Alerts</h2>
          <p className="card-subtitle">Current weather warnings and alerts for beach operations</p>
        </div>

        {alertsLoading ? (
          <div className="forecast-loading">
            <div className="loading-spinner">⏳</div>
            <p>Loading weather alerts...</p>
          </div>
        ) : (
          <div className="weather-alerts-container">
            {/* Thunderstorm Alerts */}
            <div className="alert-section">
              <div className="alert-header">
                <h3>⚡ Thunderstorm Alerts</h3>
              </div>
              {thunderstormAlerts ? (
                <div className={`alert ${thunderstormAlerts.hasAlert ? 'alert-danger' : 'alert-success'}`}>
                  <strong>
                    {thunderstormAlerts.hasAlert ? '⚠️ ACTIVE ALERT:' : '✅ All Clear:'}
                  </strong>
                  <p>{thunderstormAlerts.alertText}</p>
                  {thunderstormAlerts.thunderstormTimes && thunderstormAlerts.thunderstormTimes.length > 0 && (
                    <div className="storm-times">
                      <strong>Storm Times:</strong>
                      <ul>
                        {thunderstormAlerts.thunderstormTimes.map((storm, index) => (
                          <li key={index}>{storm.formatted}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {thunderstormAlerts.heavyRainTimes && thunderstormAlerts.heavyRainTimes.length > 0 && (
                    <div className="rain-times">
                      <strong>Heavy Rain Times:</strong>
                      <ul>
                        {thunderstormAlerts.heavyRainTimes.map((rain, index) => (
                          <li key={index}>{rain.formatted}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <div className="alert-timestamp" style={{ fontSize: '0.7rem', opacity: 0.6, marginTop: '0.5rem' }}>
                    Last updated: {thunderstormAlerts.timestamp ? new Date(thunderstormAlerts.timestamp).toLocaleString() : 'Unknown'}
                  </div>
                </div>
              ) : (
                <div className="alert alert-warning">
                  <p>Unable to fetch thunderstorm alerts</p>
                </div>
              )}
            </div>

            {/* NWS Alerts */}
            <div className="alert-section">
              <div className="alert-header">
                <h3>🌩️ National Weather Service Alerts</h3>
              </div>
              {nwsAlerts ? (
                <div className={`alert ${nwsAlerts.hasAlerts ? 'alert-warning' : 'alert-success'}`}>
                  <strong>
                    {nwsAlerts.hasAlerts ? `⚠️ ${nwsAlerts.count} Active Alert${nwsAlerts.count > 1 ? 's' : ''}:` : '✅ No Active Alerts:'}
                  </strong>
                  <div className="nws-alerts-text" style={{ marginTop: '0.5rem' }}>
                    {nwsAlerts.alertsText}
                  </div>
                  {nwsAlerts.hasAlerts && nwsAlerts.alerts && nwsAlerts.alerts.length > 0 && (
                    <div className="nws-details" style={{ marginTop: '1rem' }}>
                      {nwsAlerts.alerts.map((alert, index) => (
                        <div key={index} className="nws-alert-detail" style={{ 
                          marginBottom: '0.5rem', 
                          padding: '0.5rem', 
                          border: '1px solid #ddd', 
                          borderRadius: '4px',
                          backgroundColor: '#f8f9fa'
                        }}>
                          <div><strong>{alert.event}</strong> ({alert.severity})</div>
                          <div style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>
                            <strong>Areas:</strong> {alert.areas}
                          </div>
                          {alert.effective && alert.expires && (
                            <div style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>
                              <strong>Time:</strong> {new Date(alert.effective).toLocaleString()} - {new Date(alert.expires).toLocaleString()}
                            </div>
                          )}
                          {alert.description && (
                            <div style={{ fontSize: '0.8rem', marginTop: '0.25rem', fontStyle: 'italic' }}>
                              <strong>Description:</strong> {alert.description}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="alert-timestamp" style={{ fontSize: '0.7rem', opacity: 0.6, marginTop: '0.5rem' }}>
                    Last updated: {nwsAlerts.timestamp ? new Date(nwsAlerts.timestamp).toLocaleString() : 'Unknown'}
                  </div>
                </div>
              ) : (
                <div className="alert alert-warning">
                  <p>Unable to fetch NWS alerts</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Current Surf Conditions */}
      <div className="card">
        <SurfConditionsDashboard />
      </div>

      {/* 10-Hour Weather Forecast */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">🌤️ 7-Hour Weather Forecast</h2>
          <p className="card-subtitle">Sourced from tomorrow.io</p>
        </div>

        {forecastLoading ? (
          <div className="forecast-loading">
            <div className="loading-spinner">⏳</div>
            <p>Loading weather forecast...</p>
          </div>
        ) : (
          <div className="dashboard-forecast">
            {forecast && forecast.hourly && forecast.hourly.slice(0, 7).map((hour, index) => (
              <div key={index} className="dashboard-hour-card">
                <div className="hour-time">{formatTime(hour.time)}</div>
                <div className="hour-icon">{getWeatherIcon(hour.values.weatherCode)}</div>
                <div className="hour-temp">{celsiusToFahrenheit(hour.values.temperature)}°F</div>
                <div className="hour-details">
                  <div className="hour-detail">
                    <span className="detail-icon">💨</span>
                    <span className="detail-value">{msToMph(hour.values.windSpeed)} mph</span>
                  </div>
                  <div className="hour-detail">
                    <span className="detail-icon">💧</span>
                    <span className="detail-value">{Math.round(hour.values.precipitationProbability || 0)}%</span>
                  </div>
                  <div className="hour-detail">
                    <span className="detail-icon">☀️</span>
                    <span className="detail-value">UV {hour.values.uvIndex}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {forecast && forecast.error && (
          <div className="alert alert-warning" style={{ marginTop: '1rem' }}>
            <strong>Note:</strong> Using default weather data due to API unavailability.
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Quick Actions</h2>
        </div>

        <div className="schedule-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
          <Link to="/schedule-request" className="schedule-day" style={{ textDecoration: 'none', color: 'inherit' }}>
            <h3>📅 Schedule Request</h3>
            <p>Mark your availability for the next 14 days</p>
          </Link>

          <Link to="/availability" className="schedule-day" style={{ textDecoration: 'none', color: 'inherit' }}>
            <h3>👥 View Availability</h3>
            <p>See who has signed up for upcoming days</p>
          </Link>

          {canAccessUserManagement() && (
            <Link to="/user-management" className="schedule-day" style={{ textDecoration: 'none', color: 'inherit' }}>
              <h3>⚙️ Management</h3>
              <p>Manage employee accounts and permissions</p>
            </Link>
          )}

          {/* Quick action: Set my beach flag */}
          <div className="schedule-day" style={{ textDecoration: 'none', color: 'inherit' }}>
            <h3>🏳️ Set Beach Flag</h3>
            <p>Change the condition flag at your assigned beach</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginTop: '8px' }}>
              {[
                {k:'black', label:'Black', bg:'#111', color:'#fff'},
                {k:'green', label:'Green', bg:'#2e7d32', color:'#fff'},
                {k:'yellow', label:'Yellow', bg:'#f9a825', color:'#000'},
                {k:'red', label:'Red', bg:'#c62828', color:'#fff'}
              ].map(opt => (
                <button
                  key={opt.k}
                  onClick={() => updateMyBeachFlag(opt.k)}
                  disabled={updatingFlag}
                  style={{
                    padding: '8px',
                    border: 'none',
                    borderRadius: '6px',
                    background: opt.bg,
                    color: opt.color,
                    cursor: updatingFlag ? 'not-allowed' : 'pointer',
                    fontWeight: 700
                  }}
                >{opt.label}</button>
              ))}
            </div>
          </div>

          {/* Quick action: Set all beach flags (LT/Captain) */}
          {canSetAllFlags() && (
            <div className="schedule-day" style={{ textDecoration: 'none', color: 'inherit' }}>
              <h3>🏴‍☠️ Set All Beach Flags</h3>
              <p>Update condition flags for all beaches</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginTop: '8px' }}>
                {[
                  {k:'black', label:'Black', bg:'#111', color:'#fff'},
                  {k:'green', label:'Green', bg:'#2e7d32', color:'#fff'},
                  {k:'yellow', label:'Yellow', bg:'#f9a825', color:'#000'},
                  {k:'red', label:'Red', bg:'#c62828', color:'#fff'}
                ].map(opt => (
                  <button
                    key={opt.k}
                    onClick={() => setAllFlags(opt.k)}
                    disabled={settingAll}
                    style={{
                      padding: '8px',
                      border: 'none',
                      borderRadius: '6px',
                      background: opt.bg,
                      color: opt.color,
                      cursor: settingAll ? 'not-allowed' : 'pointer',
                      fontWeight: 700
                    }}
                  >{opt.label}</button>
                ))}
              </div>
            </div>
          )}

          {canManageStormClosure() && (
            <div 
              className="schedule-day storm-closure-action" 
              onClick={stormClosure?.isActive ? reopenBeaches : closeBeaches}
              style={{ 
                textDecoration: 'none', 
                color: 'inherit',
                cursor: stormClosureLoading ? 'not-allowed' : 'pointer',
                opacity: stormClosureLoading ? 0.6 : 1,
                background: stormClosure?.isActive ? 
                  'linear-gradient(135deg, #32CD32 0%, #228B22 100%)' : 
                  'linear-gradient(135deg, #FF6B6B 0%, #DC143C 100%)',
                border: stormClosure?.isActive ? '2px solid #228B22' : '2px solid #DC143C'
              }}
            >
              <h3>
                {stormClosure?.isActive ? '🌤️ Reopen Beaches' : '⛈️ Storm Closure'}
                {stormClosureLoading && ' ⏳'}
              </h3>
              <p>
                {stormClosure?.isActive 
                  ? 'Clear storm conditions and reopen beaches'
                  : 'Close all beaches due to dangerous weather'
                }
              </p>
              {stormClosure?.isActive && stormClosure.closure && (
                <div style={{ 
                  fontSize: '0.8rem', 
                  marginTop: '0.5rem', 
                  opacity: 0.9,
                  fontStyle: 'italic'
                }}>
                  Closed: {stormClosure.closure.reason}
                </div>
              )}
            </div>
          )}

          {canGenerateRunSheet() && (
            <div 
              className="schedule-day run-sheet-action" 
              style={{ 
                textDecoration: 'none', 
                color: 'inherit',
                background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                border: '2px solid #FFA500',
                cursor: 'default'
              }}
            >
              <h3>
                📄 Generate Run Sheet
              </h3>
              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="runSheetDate" style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontSize: '0.9rem',
                  fontWeight: 'bold',
                  color: '#333'
                }}>
                  Select Date:
                </label>
                <input
                  id="runSheetDate"
                  type="date"
                  value={runSheetDate}
                  onChange={(e) => setRunSheetDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '2px solid #FFA500',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    background: 'white',
                    color: '#333'
                  }}
                />
              </div>
              <button
                onClick={generateRunSheet}
                disabled={runSheetLoading}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: runSheetLoading ? '#ccc' : '#FF8C00',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: runSheetLoading ? 'not-allowed' : 'pointer',
                  opacity: runSheetLoading ? 0.6 : 1
                }}
              >
                {runSheetLoading ? '📄 Generating Run Sheet ⏳' : '📄 Generate Run Sheet'}
              </button>
              <p style={{ 
                fontSize: '0.85rem', 
                marginTop: '0.5rem', 
                opacity: 0.8,
                lineHeight: '1.3'
              }}>
                Generate a run sheet for any date using the most current weather data
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Your Profile</h2>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Name</label>
            <p className="mb-0">
              {user?.first_name && user?.last_name 
                ? `${user.first_name} ${user.last_name}` 
                : user?.username || 'Not provided'}
            </p>
          </div>
          <div className="form-group">
            <label className="form-label">Username</label>
            <p className="mb-0">{user?.username}</p>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Email</label>
            <p className="mb-0">{user?.email}</p>
          </div>
          <div className="form-group">
            <label className="form-label">Rank</label>
            <p className="mb-0">
              <span className="nav-rank">{user?.rank?.replace(/0$/, '')}</span>
            </p>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Beach Assignment</label>
            <p className="mb-0">{user?.beach || 'Not assigned'}</p>
          </div>
          <div className="form-group">
            <label className="form-label">Years of Service</label>
            <p className="mb-0">{user?.years_of_service || 0} years</p>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">EMT Training</label>
            <p className="mb-0">{formatBoolean(user?.emt_training)}</p>
          </div>
          <div className="form-group">
            <label className="form-label">Advanced Training</label>
            <p className="mb-0">{formatBoolean(user?.advanced_training)}</p>
          </div>
        </div>

        {user?.assigned_vehicle && (
          <div className="form-group">
            <label className="form-label">Assigned Vehicle</label>
            <p className="mb-0">{user.assigned_vehicle}</p>
          </div>
        )}
      </div>

      {/* Push Notification Management */}
      <div className="card">
        <PushNotificationManager />
      </div>

      {/* Notification Sender for Officers */}
      {canAccessUserManagement() && (
        <div className="card">
          <NotificationSender />
        </div>
      )}
    </div>
  );
};

export default Dashboard; 